import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, ShieldCheck } from 'lucide-react';

const dataCategories = [
  { label: 'Identity Data', text: 'First name, last name, and job title.' },
  { label: 'Contact Data', text: 'Business address, email address, and telephone number.' },
  { label: 'Transaction Data', text: 'Details of certification, audit, and training services you have purchased from us.' },
  { label: 'Technical Data', text: 'IP address, login data, browser type and version, time zone, location, and device/operating system information used to access this website.' },
  { label: 'Profile Data', text: 'Your account username and password (where applicable), and orders or applications made by you.' },
  { label: 'Usage Data', text: 'Information about how you use our website, products, and services.' },
  { label: 'Marketing & Communications Data', text: 'Your preferences in receiving marketing from us and your communication preferences.' },
];

const collectionMethods = [
  { label: 'Direct interactions', text: 'You give us data by filling in forms, corresponding with us by post, phone, or email, or using our services — for example when you request a quote, apply for certification, undergo an audit, subscribe to updates, or contact us with feedback.' },
  { label: 'Third-party interactions', text: 'We may receive information about you from a third party, such as a referral partner or an accreditation body, always in accordance with our legal obligations.' },
  { label: 'Automated technologies', text: 'As you use our website, we may automatically collect data using cookies, server logs, and similar technologies. You can set your browser to refuse cookies, though some parts of the site may not function properly as a result.' },
];

const usageTable = [
  { purpose: 'To register you as a new applicant or client', data: 'Identity, Contact' },
  { purpose: 'To process an application, quotation, or audit engagement', data: 'Identity, Contact, Transaction' },
  { purpose: 'To conduct audits and certification decisions on behalf of your organization', data: 'Identity, Contact, Profile, Transaction' },
  { purpose: 'To manage our relationship with you, including notifying you of changes to our terms or this policy', data: 'Identity, Contact, Profile, Marketing & Communications' },
  { purpose: 'To administer and protect our business and website (troubleshooting, data analysis, testing, system maintenance, security, hosting)', data: 'Identity, Contact, Technical' },
  { purpose: 'To respond to inquiries, complaints, and appeals submitted through the Contact page', data: 'Identity, Contact, Transaction' },
  { purpose: 'To use analytics to improve our website, services, and client experience', data: 'Technical, Usage' },
  { purpose: 'To send marketing communications about certification services that may interest you', data: 'Identity, Contact, Marketing & Communications' },
];

const disclosureParties = [
  'Accreditation bodies, such as the International Accreditation Service (IAS), who oversee and regulate our certification activities.',
  'Subcontracted auditors and technical experts engaged to help deliver certification and audit services.',
  'Service providers such as hosting providers, IT support, and software vendors who process data on our behalf.',
  'Professional advisers including lawyers, accountants, auditors, and insurers.',
  'Tax authorities, regulators, and other authorities where reporting is required by law.',
  'A buyer or successor entity, in the event we sell, transfer, or merge parts of our business.',
];

const stateRights = [
  'Confirm whether we process your personal information.',
  'Access and delete certain personal information.',
  'Correct inaccuracies in your personal information.',
  'Obtain a copy of your personal data in a portable format.',
  'Opt out of the processing of your personal data for targeted advertising, sale, or profiling that produces legal or similarly significant effects.',
  'Limit or require consent for the processing of sensitive personal data.',
];

const PrivacyPage = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-gradient-to-r from-[#003f6d] via-[#004a85] to-[#003f6d] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
          <div className="w-20 h-1 bg-[#d4af37] mb-6"></div>
          <p className="text-xl text-gray-200 max-w-3xl">
            QSC America respects your privacy and is committed to protecting your personal data.
            This policy explains what information we collect, why we collect it, and the choices
            you have.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">

          <div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Please read this policy carefully to understand how we collect, use, and protect
              your information. By accessing or using this website, you agree to this privacy
              policy. If you do not agree with our practices, please do not use this website.
              This policy may change from time to time; your continued use of the website after
              changes are posted means you accept those changes, so please check back
              periodically.
            </p>
            <p className="text-gray-700 leading-relaxed">
              When we use &ldquo;QSC America&rdquo;, &ldquo;QSC&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;
              in this policy, we mean Quality Standards Certification America, LLC.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Purpose of This Document</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This privacy policy describes how we collect and process your personal data when
              you visit our website, apply for certification, or otherwise work with us, in
              accordance with applicable U.S. federal and state data protection and privacy laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              It is important that you read this privacy policy together with any other notice we
              may provide on specific occasions when we collect or process personal data about
              you, so that you are fully aware of how and why we use your data. This policy
              supplements — and does not override — any other applicable notice.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Protection Principles</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              The personal information we hold about you will be:
            </p>
            <ul className="space-y-2">
              {[
                'Used lawfully, fairly, and in a transparent way.',
                'Collected only for valid purposes that we have clearly explained to you, and not used in any way incompatible with those purposes.',
                'Relevant to the purposes we have told you about, and limited to those purposes.',
                'Accurate and kept up to date.',
                'Kept only as long as necessary for the purposes we have told you about.',
                'Kept securely.',
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">The Data We Collect About You</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Personal data means any information relating to an identified or identifiable
              individual. We may collect, use, store, and transfer different kinds of personal
              data about you, grouped as follows:
            </p>
            <ul className="space-y-2 mb-4">
              {dataCategories.map((d) => (
                <li key={d.label} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span><strong>{d.label}:</strong> {d.text}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We may also collect, use, and share aggregated data such as statistical or
              demographic data for any purpose. Aggregated data may be derived from your
              personal data but is not considered personal data, as it does not directly or
              indirectly reveal your identity. If we combine aggregated data with your personal
              data so that it can identify you, we treat the combined data as personal data under
              this policy.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">How Is Your Personal Data Collected?</h2>
            <ul className="space-y-3">
              {collectionMethods.map((m) => (
                <li key={m.label} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span><strong>{m.label}:</strong> {m.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">How We Use Your Personal Data</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The table below sets out the ways we plan to use your personal data and the type of
              data involved.
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#003f6d] text-white">
                  <tr>
                    <th className="p-3 font-semibold">Purpose / Activity</th>
                    <th className="p-3 font-semibold">Type of Data</th>
                  </tr>
                </thead>
                <tbody>
                  {usageTable.map((row, i) => (
                    <tr key={row.purpose} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-3 text-gray-700 align-top">{row.purpose}</td>
                      <td className="p-3 text-gray-600 align-top">{row.data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Disclosures of Your Personal Data</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may share your personal data with the following parties for the purposes set out
              above:
            </p>
            <ul className="space-y-2 mb-4">
              {disclosureParties.map((p) => (
                <li key={p} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Our website may include links to third-party websites, plug-ins, and applications.
              Clicking those links may allow third parties to collect or share data about you. We
              do not control these third-party websites and are not responsible for their privacy
              practices. We encourage you to read the privacy policy of every website you visit.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">International Clients</h2>
            <p className="text-gray-700 leading-relaxed">
              QSC America provides certification services within the United States and, in
              certain cases, internationally. Where we work with clients or auditors located
              outside the United States, your personal data may be transferred to and processed
              in other countries. Where this occurs, we take appropriate steps to protect your
              personal data consistent with this policy and applicable law.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We have put in place appropriate technical and organizational measures to prevent
              your personal data from being accidentally lost, used, or accessed in an
              unauthorized way, altered, or disclosed. We limit access to your personal data to
              employees, contractors, and other third parties who have a legitimate business need
              to know, and they are subject to a duty of confidentiality.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We have procedures in place to address any suspected personal data breach and will
              notify you and any applicable regulator where we are legally required to do so.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We will only retain your personal information for as long as necessary to fulfill
              the purposes we collected it for, including to satisfy legal, accounting,
              accreditation, or reporting requirements. To determine the appropriate retention
              period, we consider the amount, nature, and sensitivity of the data, the potential
              risk of harm from unauthorized use or disclosure, the purposes for which we process
              it, and applicable legal requirements — including certification record-keeping
              obligations under our accreditation program.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Children Under the Age of 16</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website is not intended for children under 16 years of age, and we do not
              knowingly collect personal information from children under 16. If you are under 16,
              please do not use this website or provide any personal information to us, including
              your name, address, telephone number, or email address.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Depending on where you live, applicable U.S. state privacy laws may give you the
              following rights regarding your personal information:
            </p>
            <ul className="space-y-2 mb-4">
              {stateRights.map((r) => (
                <li key={r} className="flex items-start gap-2 text-gray-700">
                  <ArrowRight className="w-4 h-4 text-[#003f6d] flex-shrink-0 mt-1" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 leading-relaxed mb-3">
              You also have the right to object to our processing of your personal data,
              including for direct marketing, and to withdraw any consent you have previously
              given us. You can opt out of marketing messages at any time by following the opt-out
              link in any marketing email or by contacting us directly.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The exact scope of these rights may vary depending on your state of residence. To
              exercise any of these rights, please contact us using the details below.
            </p>
          </div>

          <Card className="bg-[#003f6d]/5 border-none">
            <CardContent className="p-6 flex items-start gap-4">
              <ShieldCheck className="w-8 h-8 text-[#003f6d] flex-shrink-0 mt-1" />
              <div>
                <p className="text-gray-800 font-medium">Questions about this Privacy Policy?</p>
                <p className="text-gray-600 text-sm mt-1">
                  To exercise your privacy rights, ask a question, or raise a concern about how we
                  handle your personal data, contact us at{' '}
                  <a href="mailto:info@qscamerica.com?subject=Privacy%20Policy%20Enquiry" className="text-[#003f6d] font-medium hover:underline">
                    info@qscamerica.com
                  </a>{' '}
                  or by mail at 5500 Cherokee Ave. Ste 100, Alexandria, VA 22312, or visit our{' '}
                  <Link to="/contact" className="text-[#003f6d] font-medium hover:underline">
                    Contact page
                  </Link>. We aim to respond to all privacy requests within 30 days.
                </p>
              </div>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
